import React, { useState, useEffect } from "react";
import "./App.css";
import Converter from "./components/Converter";
import AdSense from "./components/AdSense";
import { LEGAL_CONTENT } from "./constants/legal";
import { GUIDE_CONTENT } from "./constants/guides";
import {
  Zap,
  ShieldCheck,
  Monitor,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

type View =
  | "home"
  | "about"
  | "privacy"
  | "terms"
  | "guide_formats"
  | "guide_ecommerce"
  | "guide_favicon"
  | "guide_compression"
  | "guide_cwv";

function App() {
  const [currentView, setCurrentView] = useState<View>("home");
  const [activeFormatIndex, setActiveFormatIndex] = useState(0);

  const formatCards = [
    {
      format: "WebP",
      bestFor: "Sites e aplicações web",
      summary:
        "Formato moderno com compressão eficiente. Em muitos cenários, gera arquivos menores sem comprometer a qualidade visual.",
      useCases: [
        "Imagens de produto em e-commerce",
        "Banners e imagens de blog",
        "Páginas com foco em desempenho e SEO",
      ],
      tip: "Para uso na web, geralmente é a melhor escolha para reduzir o tempo de carregamento.",
    },
    {
      format: "PNG",
      bestFor: "Transparência e elementos gráficos",
      summary:
        "Mantém alta fidelidade visual e suporte a fundo transparente. O arquivo costuma ser maior que WebP e JPG.",
      useCases: [
        "Logotipos com fundo transparente",
        "Ícones e mockups de interface",
        "Artes com texto e bordas nítidas",
      ],
      tip: "Use PNG quando transparência e fidelidade visual forem prioridade.",
    },
    {
      format: "JPG / JPEG",
      bestFor: "Fotos e compartilhamento rápido",
      summary:
        "Boa compressão para fotografias. Reduz o tamanho do arquivo, com possível perda de qualidade.",
      useCases: [
        "Fotos para redes sociais",
        "Galerias de imagens e portfólios",
        "Envio por e-mail e aplicativos de mensagem",
      ],
      tip: "Evite JPG para imagens com transparência ou com textos muito pequenos.",
    },
    {
      format: "ICO",
      bestFor: "Favicon e atalhos do navegador",
      summary:
        "Formato tradicional de ícone para abas, favoritos e atalhos do sistema.",
      useCases: [
        "favicon.ico do site",
        "Ícones de atalho no desktop",
        "Compatibilidade com navegadores antigos",
      ],
      tip: "Use ICO para o favicon principal e mantenha PNG/WebP para os demais contextos.",
    },
  ];

  const guideLinks: Array<{
    view: Exclude<View, "home" | "about" | "privacy" | "terms">;
    title: string;
    description: string;
  }> = [
    {
      view: "guide_formats",
      title: "WebP vs JPG vs PNG: qual usar em cada caso",
      description:
        "Entenda, de forma prática, qual formato entrega melhor resultado para cada tipo de imagem.",
    },
    {
      view: "guide_ecommerce",
      title: "Como preparar imagens para e-commerce",
      description:
        "Boas práticas para vitrine e página de produto, com imagens mais leves sem perder qualidade.",
    },
    {
      view: "guide_favicon",
      title: "Como criar favicon correto (ICO, PNG e tamanhos ideais)",
      description:
        "Guia objetivo para configurar favicon com compatibilidade e aparência profissional.",
    },
    {
      view: "guide_compression",
      title: "Como reduzir o peso da imagem sem perder qualidade visual",
      description:
        "Estratégias de compressão e escolha de formato para melhorar desempenho sem comprometer a apresentação.",
    },
    {
      view: "guide_cwv",
      title: "Checklist de imagens para melhorar Core Web Vitals",
      description:
        "Lista direta para otimizar LCP, reduzir desperdício de bytes e melhorar a experiência de carregamento.",
    },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentView]);

  const goToPreviousFormat = () => {
    setActiveFormatIndex((prev) =>
      prev === 0 ? formatCards.length - 1 : prev - 1
    );
  };

  const goToNextFormat = () => {
    setActiveFormatIndex((prev) =>
      prev === formatCards.length - 1 ? 0 : prev + 1
    );
  };

  const isGuideView = currentView.startsWith("guide_");

  const renderContent = () => {
    if (currentView === "home") {
      return (
        <>
          <section className="conversion-area">
            <Converter />
          </section>

          <AdSense
            className="ad-slot"
            slot="8465213790"
            style={{ margin: "2rem 0" }}
          />

          <section className="seo-content">
            <div className="seo-section">
              <h2>Por que converter WebP para PNG transparente?</h2>
              <p>
                O formato WebP é excelente para a web, mas nem todos os editores
                ou plataformas aceitam esse padrão de forma nativa. Ao{" "}
                <strong>converter WebP em PNG transparente</strong>, você preserva
                a qualidade e mantém o fundo transparente, recurso essencial para
                designers, profissionais de marketing e desenvolvedores front-end.
              </p>
            </div>

            <div className="seo-section grid-features">
              <div className="feature-card">
                <Zap className="feature-icon" />
                <h3>Conversão em lote</h3>
                <p>
                  Envie até 20 imagens de uma só vez e baixe tudo em um único
                  arquivo ZIP. Isso reduz o trabalho manual em tarefas recorrentes.
                </p>
              </div>
              <div className="feature-card">
                <ShieldCheck className="feature-icon" />
                <h3>Privacidade total</h3>
                <p>
                  Suas imagens não saem do seu computador. O processamento ocorre
                  localmente no navegador, com HTML5 Canvas.
                </p>
              </div>
              <div className="feature-card">
                <Monitor className="feature-icon" />
                <h3>Otimizado para desempenho</h3>
                <p>
                  Converta arquivos para WebP para reduzir peso e melhorar o
                  carregamento das páginas e os Core Web Vitals.
                </p>
              </div>
            </div>

            <div className="seo-section">
              <h2>Como converter WebP para JPG rapidamente?</h2>
              <ol className="how-to-list">
                <li>Arraste seus arquivos WebP para a área de upload acima.</li>
                <li>Escolha o formato de saída desejado (PNG, JPG, WebP ou ICO).</li>
                <li>Clique em "Converter tudo" para processar os arquivos.</li>
                <li>Baixe individualmente ou use "Baixar ZIP" para o lote completo.</li>
              </ol>
            </div>

            <div className="seo-section">
              <h2>Quando usar cada formato de imagem?</h2>
              <div className="format-carousel">
                <div className="carousel-nav-button-container">
                  <button
                    className="carousel-nav"
                    onClick={goToPreviousFormat}
                    aria-label="Card anterior"
                  >
                    <ChevronLeft size={20} />
                  </button>
                </div>

                <article className="format-card">
                  <p className="format-label">{formatCards[activeFormatIndex].format}</p>
                  <h3>{formatCards[activeFormatIndex].bestFor}</h3>
                  <p>{formatCards[activeFormatIndex].summary}</p>
                  <ul className="format-use-cases">
                    {formatCards[activeFormatIndex].useCases.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <p className="format-tip">
                    Dica: {formatCards[activeFormatIndex].tip}
                  </p>
                </article>

                <div className="carousel-nav-button-container">
                  <button
                    className="carousel-nav"
                    onClick={goToNextFormat}
                    aria-label="Próximo card"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>

              <div className="carousel-dots">
                {formatCards.map((card, index) => (
                  <button
                    key={card.format}
                    className={`carousel-dot ${
                      activeFormatIndex === index ? "active" : ""
                    }`}
                    onClick={() => setActiveFormatIndex(index)}
                    aria-label={`Ver card ${card.format}`}
                  />
                ))}
              </div>
            </div>

            <div className="seo-section">
              <h2>Guias práticos sobre conversão de imagem</h2>
              <div className="guide-links-grid">
                {guideLinks.map((guide) => (
                  <article key={guide.view} className="guide-link-card">
                    <button
                      className="guide-link-title"
                      onClick={() => setCurrentView(guide.view)}
                    >
                      {guide.title}
                    </button>
                    <p>{guide.description}</p>
                  </article>
                ))}
              </div>
            </div>

            <div className="seo-section">
              <h2>Perguntas frequentes</h2>
              <h3>O BoraConverter envia meus arquivos para servidor?</h3>
              <p>
                Não. A conversão acontece no navegador do usuário e os arquivos
                não são enviados para nossos servidores.
              </p>
              <h3>Existe limite de uso?</h3>
              <p>
                Sim. O limite atual é de 20 arquivos por lote, para manter bom
                desempenho durante o processamento local.
              </p>
              <h3>A qualidade da imagem muda após a conversão?</h3>
              <p>
                Depende do formato de destino. PNG tende a manter maior fidelidade
                visual; JPG pode reduzir tamanho com alguma perda; WebP costuma
                equilibrar bem qualidade e compressão.
              </p>
            </div>
          </section>
        </>
      );
    }

    const PAGE_CONTENT = { ...LEGAL_CONTENT, ...GUIDE_CONTENT } as const;
    const content = PAGE_CONTENT[currentView as keyof typeof PAGE_CONTENT];

    return (
      <section className="legal-view-container animate-fade-in">
        <button className="back-btn" onClick={() => setCurrentView("home")}>
          <ArrowLeft size={18} /> Voltar para o conversor
        </button>
        <div className="legal-card">
          <h1>{content.title}</h1>
          <div
            className="legal-body"
            dangerouslySetInnerHTML={{ __html: content.content }}
          />
          {isGuideView && (
            <>
              <hr />
              <p className="guide-meta">
                <strong>Autor:</strong> Marcus Garcia
                <br />
                <strong>Publicado em:</strong> 16 de abril de 2026
              </p>
              <a
                href="https://www.linkedin.com/in/marcus-garcia-81b529140/"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn de Marcus Garcia"
                className="guide-linkedin-badge"
              >
                Linked<span>in</span>
              </a>
            </>
          )}
        </div>
      </section>
    );
  };

  return (
    <div className="App">
      <header>
        <div
          className="brand-container"
          onClick={() => setCurrentView("home")}
          style={{ cursor: "pointer" }}
        >
          <img src="/logo.png" alt="Logotipo BoraConverter" className="brand-logo" />
        </div>
        <h1
          onClick={() => setCurrentView("home")}
          style={{ cursor: "pointer" }}
        >
          {currentView === "home"
            ? "Conversor de Imagens Grátis e 100% Online"
            : "Centro de Informações"}
        </h1>
        {currentView === "home" && (
          <p className="subtitle">
            Converta imagens WebP, PNG, JPG e favicon instantaneamente,
            com processamento local e foco em privacidade.
          </p>
        )}
      </header>

      <main>{renderContent()}</main>

      <footer>
        <div className="footer-links">
          <span>BORACONVERTER &copy; 2026</span> |
          <button className="flat-link" onClick={() => setCurrentView("about")}>
            Sobre
          </button>
          |
          <button className="flat-link" onClick={() => setCurrentView("privacy")}>
            Privacidade
          </button>
          |
          <button className="flat-link" onClick={() => setCurrentView("terms")}>
            Termos
          </button>
        </div>
        <p className="footer-note">Canal de atendimento em fase de implantação.</p>
      </footer>
    </div>
  );
}

export default App;